/**
 * Minimal Service Worker registration.
 *
 * Behaviour:
 *   • Only runs in production builds (`NODE_ENV === 'production'`). The
 *     Emergent preview env runs `yarn start` (dev server) — registering a
 *     SW there would intercept HMR and break the dev workflow.
 *   • Detects new SW versions after a deploy and asks the new SW to take
 *     over immediately. Combined with the navigation-network-first
 *     strategy in `sw.js`, this means a redeploy is reliably picked up on
 *     the very next page load.
 *   • Falls back silently on any error — we never want PWA plumbing to
 *     block the app from booting.
 */

export function registerServiceWorker() {
  if (process.env.NODE_ENV !== 'production') return;
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  // Register after `load` so SW install never competes with first paint.
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        // When an update is found, tell the new worker to activate ASAP
        // (no "close every tab" dance for users). The activate handler in
        // sw.js calls clients.claim() so the new SW immediately controls
        // open pages.
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              installing.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch(() => {
        // Swallow — SW failure must never block the app.
      });

    // Reload once when the active SW changes so the user gets the latest
    // bundle on next interaction. Guarded against reload loops.
    let refreshed = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshed) return;
      refreshed = true;
      // Slight delay so any in-flight navigation completes first.
      window.setTimeout(() => window.location.reload(), 0);
    });
  });
}
